import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CustomSelect from "../../Components/common/CustomSelect/CustomSelect";
import ReactTableWrapper from "../../Components/common/ReactTableWrapper/ReactTableWrapper";
import { getStoredPageSize } from "../../Components/common/PageSizeSelect/PageSizeSelect";
import TabButton from "../../Components/common/TabButton/TabButton";
import SearchIcon from "../../Components/Icon-SVG/SearchIcon";
import IconXMarkIcon from "../../Components/Icon-SVG/IconXMarkIcon";
import TransferLogDetail from "./components/TransferLogDetail";

import { mailTransferLogsHeader } from "../../utils/constants/tableHeaders";
import { RequestIndexes } from "../../store/actions/zsearchActions";
import { RequestTransferLogs } from "../../store/actions/mailGatewayActions";

import "./styles.scss";

const PAGE_SIZE = 10;
const TABLE_NAME = "mailTransferLogs";
const SEARCH_DELAY_MS = 500;
const FETCH_DELAY_MS = 300;

// Tab title -> postfix status the backend filters on ("" = every status)
const STATUS_TABS = [
  { title: "All", status: "", count: "all" },
  { title: "Delivered", status: "sent", count: "sent" },
  { title: "Deferred", status: "deferred", count: "deferred" },
  { title: "Bounced", status: "bounced", count: "bounced" },
  { title: "Not Delivered", status: "timeout", count: "timeout" },
];

const TransferLogs = () => {
  const dispatch = useDispatch();

  const mgwDevices = useSelector((state) => state.zsearch.mgwDevices);
  const mgwDevicesLoading = useSelector((state) => state.zsearch.mgwDevicesLoading);
  const logs = useSelector((state) => state.mailGateway.transferLogs);
  const loading = useSelector((state) => state.mailGateway.transferLogsLoading);
  const pageCount = useSelector((state) => state.mailGateway.transferLogsPageCount);
  const total = useSelector((state) => state.mailGateway.transferLogsTotal);
  const statusCounts = useSelector((state) => state.mailGateway.transferLogsStatusCounts);
  const truncated = useSelector((state) => state.mailGateway.transferLogsTruncated);

  const [selectedHost, setSelectedHost] = useState(null);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(() => getStoredPageSize(TABLE_NAME, PAGE_SIZE));
  const [statusTab, setStatusTab] = useState(STATUS_TABS[0].title);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [tableFilters, setTableFilters] = useState([]);

  useEffect(() => {
    dispatch(RequestIndexes("postfix."));
  }, [dispatch]);

  // The typed text is applied once typing pauses, from the first page
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== search) {
        setSearch(searchInput.trim());
        setPageNumber(1);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const status = STATUS_TABS.find((tab) => tab.title === statusTab)?.status;

  const requestLogs = () =>
    dispatch(
      RequestTransferLogs({
        host: selectedHost.id,
        selectedTimeFilter,
        pageNumber: pageNumber < 1 ? 1 : pageNumber,
        pageSize,
        search,
        statusTab: status,
        filters: tableFilters,
      }),
    );

  // Column searches and page clicks come in bursts; one request per pause
  useEffect(() => {
    if (!selectedHost?.id || !selectedTimeFilter) return undefined;
    const timer = setTimeout(requestLogs, FETCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [selectedHost, selectedTimeFilter, pageNumber, pageSize, search, status, tableFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshHandler = () => {
    if (selectedHost?.id && selectedTimeFilter) requestLogs();
  };

  const tabChangeHandler = (title) => {
    if (title === statusTab) return;
    setStatusTab(title);
    setPageNumber(1);
  };

  const filterByTime = useMemo(
    () => ({
      show: true,
      label: "Filter by last: ",
      defaultIndex: 2,
      selectedTimeFilter,
      setSelectedTimeFilter,
    }),
    [selectedTimeFilter],
  );

  return (
    <div className="mgw-transfer">
      <div className="mgw-transfer__header">
        <h1>Mail Transfer Logs</h1>
        <p>
          Search page at MTA logs of all messages sent and received by the Cyber Fortress Mail Gateway.
        </p>
        <div className="mgw-transfer__header__select">
          <p>Select host: </p>
          <CustomSelect
            id="mgwTransferLogs"
            data={mgwDevices.length ? mgwDevices : []}
            select={selectedHost}
            setSelected={setSelectedHost}
            width={220}
            disabled={mgwDevices.length === 0}
            placeholder={mgwDevicesLoading ? "Fetching data..." : "No data."}
          />
        </div>
      </div>

      <div className="mgw-transfer__search border">
        <SearchIcon width={16} height={16} />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by sender, recipient, subject, queue ID, message-ID, next hop, DSN or server response"
          aria-label="Search transfer logs"
        />
        {searchInput && (
          <button
            type="button"
            className="mgw-transfer__search__clear"
            onClick={() => setSearchInput("")}
            title="Clear search"
          >
            <IconXMarkIcon />
          </button>
        )}
      </div>
      <div className="mgw-transfer__meta">
        <p>
          All words must match, in any of the fields.
        </p>
        {selectedHost && (
          <p className="mgw-transfer__meta__count">
            {truncated
              ? `${total.toLocaleString()} records match; the first 10,000 can be paged`
              : total
                ? `${total.toLocaleString()} transfer record${total === 1 ? "" : "s"}`
                : loading
                  ? ""
                  : "No transfer records match"}
          </p>
        )}
      </div>

      <div className="mgw-transfer__table border">
        <div className="button-block">
          {STATUS_TABS.map((tab) => (
            <TabButton
              key={tab.title}
              title={tab.title}
              tabDisplay={`${tab.title} (${(statusCounts[tab.count] || 0).toLocaleString()})`}
              onClick={tabChangeHandler}
              active={statusTab}
            />
          ))}
        </div>
        <ReactTableWrapper
          data={logs}
          isDataLoading={loading}
          tableName={TABLE_NAME}
          columns={mailTransferLogsHeader}
          paginationButtons
          customPageSize={PAGE_SIZE}
          refreshConfig={{ show: true, onClickHandler: refreshHandler }}
          expandConfig={{
            use: true,
            expandOnRowClick: true,
            subComponent: TransferLogDetail,
          }}
          pagination={{
            manualPagination: true,
            totalPageCount: pageCount,
            serverSidePaginationNumber: pageNumber,
            setServerSidePaginationNumber: setPageNumber,
            onPageSizeChange: setPageSize,
          }}
          filterByTime={filterByTime}
          onFilterChange={setTableFilters}
        />
      </div>
    </div>
  );
};

export default TransferLogs;
